import { useState } from 'react'
import classNames from 'classnames'
import parse from 'html-react-parser'
import { useRouter } from 'next/router'
import { Trans, useTranslation } from 'next-i18next/pages'
import type { MoveProps } from 'features/move/types'
import styles from './Move.module.scss'
import { Button, Header, Toggle, StepList, Video } from '@boltenergy-be/design-system'
import { InputGroupKeys, MeterDetailsFieldKeys } from 'store/customer/enums'
import { updateInput } from 'store/customer/slice'
import { useStoreDispatch, useStoreSelector } from 'hooks/store'
import { Heading, Img } from '@boltenergy-be/design-system'
import { BOLT_EMAIL, BOLT_PHONE, PORTAL_URL, TAKEOVER_DOCUMENT_URL } from 'constants/constants'
import { Language } from 'types/language'
import { startSimulationOrRegistration } from 'utils/customerFlow'
import { updateMarketingData } from 'store/marketing/slice'
import { MarketingDataKeys } from 'store/marketing/types'
import Link from 'components/link/Link'
import FaqWithDefaultDescription from 'components/faq-with-default-description/FaqWithDefaultDescription'
import { SITUATIONS } from '@boltenergy-be/shared'

const Move = ({ categories }: MoveProps) => {
  // Redux store
  const { didSimulation } = useStoreSelector((store) => store.customer.flows.simulation)
  const dispatch = useStoreDispatch()

  // i18n
  const { t } = useTranslation(['move', 'common'])

  // Router
  const router = useRouter()

  // Local state
  const [isClient, setIsClient] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const handleButtonClick = async () => {
    setIsLoading(true)

    if (!isClient) {
      dispatch(updateInput({ group: InputGroupKeys.METER_DETAILS, key: MeterDetailsFieldKeys.SITUATION, value: SITUATIONS.move }))
      dispatch(updateMarketingData({ key: MarketingDataKeys.SIMULATION_SO, value: 'Move Page' }))
      await startSimulationOrRegistration()
    } else {
      return window.open(`${PORTAL_URL}/contracts`, '_blank')
    }

    setIsLoading(false)
  }

  return (
    <main>
      <Header docked color="accent-soft" title={parse(t('hero.title', 'Regel je verhuis,<br />vlot en zonder gedoe'))}>
        <p>
          {t(
            'hero.description',
            'Je verhuist binnenkort en wil duurzame stroom van één van onze lokale energie-opwekkers in je nieuwe woning? Topidee! We zorgen ervoor dat je dit gemakkelijk zelf kan regelen en helpen je waar nodig.'
          )}
        </p>
        <Link representation="button" as="a" trailingIcon="arrowBottom" variant="layered" href="#how-it-works">
          {t('hero.cta', 'Wat moet je doen?')}
        </Link>
      </Header>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className={classNames('section', styles.section, styles['how-it-works'])}>
        <div className={classNames('container', styles.container)}>
          <div className={styles.content}>
            <Heading as="h2" variant="d3">
              {t('howItWorks.title', 'Hoe werkt het?')}
            </Heading>

            <Toggle
              options={[
                { label: t('howItWorks.nonClient.label', 'Ik ben nog geen klant'), value: false },
                { label: t('howItWorks.client.label', 'Ik ben al klant'), value: true }
              ]}
              active={isClient}
              onClick={(value) => setIsClient(value)}
            />

            <StepList size="large">
              {(
                t(isClient ? 'howItWorks.client.steps' : 'howItWorks.nonClient.steps', { returnObjects: true }) as Record<string, string>[]
              ).map((step, index) => (
                <li key={`how-it-works-${index}`}>
                  <div className="flex-col gap-100">
                    <Heading as="h2" variant="h4" serif>
                      {step.title}
                    </Heading>
                    <p>{step.shortDescription}</p>
                  </div>
                </li>
              ))}
            </StepList>

            <div className="flex-col gap-500">
              <Button isFullwidthMobile className={styles.simulate} loading={isLoading} onClick={handleButtonClick}>
                {isClient
                  ? t('howItWorks.client.action', 'Geef een verhuis door')
                  : didSimulation
                    ? t('howItWorks.nonClient.personalizedOffer', 'Bekijk lokale energie-opwekkers')
                    : t('howItWorks.nonClient.action', 'Bereken het voorschot voor je nieuw adres')}
              </Button>

              <small className="text-subdued">
                <Trans
                  {...{ t }}
                  i18nKey="howItWorks.footer"
                  components={{
                    tel: <Link href={`tel:${BOLT_PHONE}`} as="a" variant="inline" />,
                    mail: <Link href={`mailto:${BOLT_EMAIL[router.locale as Language]}`} as="a" variant="inline" />
                  }}
                />
              </small>
            </div>
          </div>

          <Img publicId="website/move/moving-lady" width={472} height={500} className={styles.img} alt="" />
        </div>
      </section>

      {/* EOD */}
      <section className={classNames('section is-dark', styles['eod-section'])}>
        <div className="container">
          <Heading as="h3" variant="h4">
            {t('eod.title', 'Houd je overnamedocument bij de hand')}
          </Heading>

          <p className="mt-200 mb-500">
            {t(
              'eod.description',
              'Een energieovernamedocument is het document dat je best altijd invult met de nieuwe bewoner van je oude woning of de vorige bewoner van je toekomstige woonplek. Je legt zo de meterstanden en de juiste datum van overname definitief vast. Een energieovernamedocument versnelt ook de overstapprocedure en zorgt voor transparantie voor zowel jou als consument, als voor de energieleverancier. Het document is niet verplicht, maar heeft juridische waarde in geval van een geschil. '
            )}
          </p>

          <Link representation="button" external href={TAKEOVER_DOCUMENT_URL[router.locale as Language]} target="_blank">
            {t('eod.download', 'Download energieovernamedocument')}
          </Link>
        </div>
      </section>

      {/* VIDEO */}
      <section className="section container">
        <figure className={styles['video-wrapper']}>
          <Video thumbnailOffset="6" publicId={`website/videos/move-${router.locale === Language.FRENCH ? 'fr' : 'nl'}`} />
          <figcaption>
            {t(
              'video.caption',
              'Een verhuis regelen, hoe werkt dat? En een energieovername-document, wat is dat ook alweer? Hope legt het uit.'
            )}
          </figcaption>
        </figure>
      </section>

      {/* FAQ */}
      <FaqWithDefaultDescription categories={categories} title={t('move:faq.title')} />
    </main>
  )
}

export default Move
